import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Users, UserPlus, Loader2, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsuariosCliente = () => {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const [cliente, setCliente] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [clienteId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [clienteRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/admin/clientes-empresa/${clienteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/clientes-empresa/${clienteId}/usuarios`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCliente(clienteRes.data);
      setUsuarios(usuariosRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <Button onClick={() => navigate(`/admin/crear-cliente?cliente=${clienteId}`)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Agregar Usuario
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {cliente && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-2">{cliente.nombre}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">RUT</p>
                  <p className="font-medium">{cliente.rut}</p>
                </div>
                <div>
                  <p className="text-gray-600">Teléfono</p>
                  <p className="font-medium">{cliente.telefono}</p>
                </div>
                <div>
                  <p className="text-gray-600">Contacto</p>
                  <p className="font-medium">{cliente.nombre_contacto}</p>
                </div>
                <div>
                  <p className="text-gray-600">Estado</p>
                  <Badge className={cliente.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {cliente.estado}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuarios del Cliente ({usuarios.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usuarios.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No hay usuarios registrados para este cliente</p>
                <Button onClick={() => navigate(`/admin/crear-cliente?cliente=${clienteId}`)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Crear Primer Usuario
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">Nombre</th>
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Teléfono</th>
                      <th className="text-left p-3 font-semibold">Estado</th>
                      <th className="text-left p-3 font-semibold">Fecha Registro</th>
                      <th className="text-right p-3 font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.map((usuario) => (
                      <tr key={usuario.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{usuario.nombre_completo}</td>
                        <td className="p-3 text-sm">{usuario.email}</td>
                        <td className="p-3 text-sm">{usuario.telefono || '-'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            usuario.estado === 'activo' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {usuario.estado}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(usuario.created_at).toLocaleDateString('es-ES')}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/clientes/${usuario.id}/editar`)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UsuariosCliente;